import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { LoadingSpinner, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useHistory } from "react-router-dom";

import { PullToRefresh } from "../ShiftDiscovery/ListView/PullToRefresh";
import { SHIFT_DISCOVERY_LIST_PATH } from "../ShiftDiscovery/paths";
import { useScrollToTopOnStatusTap } from "../useScrollToTopOnStatusTap";
import { InHomeBaaDialog } from "./BaaDialog/Dialog";
import { useSignatures } from "./BaaDialog/useSignatures";
import { BAA_SIGNATURE_NAME } from "./constants";
import { InHomeOpenVisitsList } from "./OpenVisits/List";
import {
  getScreeningSurveyStatusQueryKey,
  useGetScreeningSurveyStatus,
} from "./ScreeningSurvey/api/useGetScreeningSurveyStatus";
import { ScreeningSurveyFlow } from "./ScreeningSurvey/ScreeningSurveyFlow";
import { ScreeningSurveyPassed } from "./ScreeningSurvey/ScreeningSurveyPassed";
import { ScreeningSurveyWaitlist } from "./ScreeningSurvey/ScreeningSurveyWaitlist";
import { getVisitsQueryKey } from "./useGetVisitsWithCaseData";
import { getWorkerVisitLockQueryKey } from "./useGetWorkerVisitLockStatus";

export function FindVisitsPage() {
  const isScreeningSurveyEnabled = useCbhFlag(CbhFeatureFlag.HOME_HEALTH_SCREENING_SURVEY_ENABLED, {
    defaultValue: false,
  });

  if (!isScreeningSurveyEnabled) {
    return <FindVisitsPageContent />;
  }

  return <ScreeningSurveyGate />;
}

function ScreeningSurveyGate() {
  const { userId: workerId } = useDefinedWorker();
  const { data, isLoading } = useGetScreeningSurveyStatus(workerId);
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingSpinner size="medium" />
      </Box>
    );
  }

  const status = data?.data?.attributes?.status;

  switch (status) {
    case "EXEMPT":
    case "PASSED": {
      return <FindVisitsPageContent />;
    }

    case "FAILED": {
      return <ScreeningSurveyWaitlist />;
    }

    case "PENDING":
    default: {
      return (
        <ScreeningSurveyFlow
          onComplete={async () => {
            await queryClient.invalidateQueries(getScreeningSurveyStatusQueryKey(workerId));
          }}
        />
      );
    }
  }
}

function FindVisitsPageContent() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { userId: workerId } = useDefinedWorker();

  const queryClient = useQueryClient();

  useScrollToTopOnStatusTap(scrollRef);
  const {
    data: signaturesData,
    isSuccess: isSuccessSignatures,
    isLoading: isLoadingSignatures,
  } = useSignatures();
  const baaModalState = useModalState("closed");
  const hasSignedBaa =
    isSuccessSignatures &&
    signaturesData.data.some(
      (signature) => signature.attributes.documentName === BAA_SIGNATURE_NAME
    );

  if (isLoadingSignatures) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingSpinner size="medium" />
      </Box>
    );
  }

  if (isSuccessSignatures && !hasSignedBaa && baaModalState.modalIsClosed) {
    return <ScreeningSurveyPassed onNext={baaModalState.openModal} />;
  }

  return (
    <>
      {isSuccessSignatures && <InHomeBaaDialog modalState={baaModalState} />}
      <Stack
        sx={(theme) => ({
          flex: 1,
          backgroundColor: theme.palette.background.tertiary,
          overflow: "hidden",
          position: "relative",
        })}
      >
        <StickyScrollPageHeader
          title="In-Home Visits"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          navigateBack={() => {
            history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
          }}
        />
        <PullToRefresh
          scrollRef={scrollRef}
          onRefresh={async () => {
            await Promise.all([
              queryClient.invalidateQueries(getVisitsQueryKey),
              queryClient.invalidateQueries(getWorkerVisitLockQueryKey(workerId)),
            ]);
          }}
        />
        <Stack
          ref={scrollRef}
          data-testid="home-health-find-visits-page-scroll-container"
          spacing={7}
          sx={{
            flex: 1,
            overflow: "auto",
            px: 5,
            pt: 6,
          }}
        >
          {isSuccessSignatures && hasSignedBaa && <InHomeOpenVisitsList />}
        </Stack>
      </Stack>
    </>
  );
}
